import { FilterPills, SearchField, Skeleton, useErrorToast } from '@h2hspark/ui';
import { Info, SearchX } from 'lucide-react';
import * as React from 'react';

import { useMemberSession } from '@/auth/auth-provider';

import { useGoingCounts } from '../attendee-preview/api';
import { useOwnProfile } from '../members/api';

import { useDiscoverEvents } from './api';
import { DISCOVER_SIZE_FILTER_LABEL, emptyListCopy, greetingFor } from './constants';
import { EventCard } from './event-card';

/**
 * Discover — the member app's landing surface (`MEM-014`, and `MEM-019` AC-1's signed-out half).
 *
 * ## Built from `Web App.dc.html` (10 September), `showDiscover` (lines 234–260)
 *
 * The **heading row**: the greeting (`font-brand 600`, `var(--text-sm)`, `--text-muted`) over the
 * served heading at `var(--text-4xl)`, and at the right the **search pill** — 46px, pill-shaped,
 * 320px (`SearchField size="member"`, with its clear control). Then the **atmosphere chips**
 * (`FilterPills size="member"`, `margin:20px 0 24px`), then a **two-column grid** of event cards
 * with a 24px gap. On the phone the design draws no breakpoint at all, so the reflow is ours
 * (`MEM-056`): the search pill drops beneath the heading at full width and the grid is one
 * column. Both change at `md`, the one width the member app changes layout at.
 *
 * ## The order is the AC
 *
 * `MEM-014` AC-1 lists it: a greeting, a search field, an atmosphere filter row, then the list of
 * published events — and the markup keeps that order at every width, so a screen reader and the
 * phone read it as the criterion does even where the desktop sets the search beside the heading.
 *
 * ## Where the build does better than the prototype, kept (`handoff-corrections.md` #122)
 *
 * The prototype's chips are static spans with *All* always selected, its greeting is always *Good
 * morning*, and a query that matches only a past event gives an empty grid with no message. The
 * chips here filter (`MEM-014` AC-7), the greeting follows the clock, and an empty result says so
 * with the API's own sentence (AC-5).
 *
 * ## Signed out is not a degraded version of this screen
 *
 * `MEM-019` AC-1: the list, the search and the filter are available with no account. What a
 * signed-out visitor does not get is the greeting's name and the going count on each card — the
 * count is the roster's figure and `MEM-019` AC-2 keeps it for members, so it is not even asked
 * for without a session.
 */
export function DiscoverPage(): React.ReactElement {
  const [search, setSearch] = React.useState('');
  const [atmosphere, setAtmosphere] = React.useState('all');
  const { session } = useMemberSession();
  const showError = useErrorToast();

  const query = useDiscoverEvents({ search, atmosphere });
  const response = query.data;
  const eventIds = React.useMemo(
    () => (response?.items ?? []).map((card) => card.eventId),
    [response?.items],
  );
  const goingCounts = useGoingCounts(eventIds, { enabled: session !== null });
  const goingByEvent = React.useMemo(
    () =>
      new Map(
        (goingCounts.data?.goingCounts ?? []).map((count) => [count.eventId, count.goingCount]),
      ),
    [goingCounts.data],
  );

  React.useEffect(() => {
    if (query.error !== null) showError(query.error, 'We could not load what is on.');
  }, [query.error, showError]);

  /**
   * **The greeting's name is the member profile's, never the sign-in record's** — TW 44164929
   * (https://ten243.teamwork.com/app/tasks/44164929).
   *
   * This read `session.displayName`, and for an account created minutes earlier that field is the
   * **local part of the email address**: `session-enrichment.ts` puts it there because there is no
   * name to put there yet. So Brad's screen said *"Good afternoon, brad.gustavesen+leslie"* while
   * the avatar three inches above it said **LC** — the real name, which the header has read off
   * the member profile since TW 44131893. The greeting was the last holdout.
   *
   * It now reads the same query the avatar reads, so the two agree by construction and a name
   * saved on Your account reaches both the moment that mutation replaces the query.
   *
   * **The fallback is no name at all, not the session's.** Falling back to `displayName` would
   * reintroduce the defect for exactly the member it was reported for: a brand-new account whose
   * profile has not arrived yet is the one account whose `displayName` is an email address.
   * *"Good afternoon"* is a sentence the signed-out screen has always used (`MEM-019` AC-1), so
   * the absence already has a shape here and needs no new copy.
   */
  const ownProfile = useOwnProfile({ enabled: session !== null });
  const firstName = ownProfile.data?.shared.firstName;
  const greeting = greetingFor(new Date(), firstName);
  const activeFilterLabel =
    response?.atmosphereFilters.find(
      (filter) => filter.value === atmosphere && filter.value !== 'all',
    )?.label ?? null;

  return (
    <div>
      {/* 1 and 2 — the greeting, then the search field: beside each other on the desktop, one
          above the other on the phone. */}
      <div className="flex flex-col gap-4 md:mb-2 md:flex-row md:items-end md:justify-between md:gap-6">
        <header className="min-w-0">
          <p className="font-brand text-sm font-semibold text-muted-foreground">{greeting}</p>
          <h1 className="mt-1 text-2xl tracking-tight md:text-4xl">
            {response?.heading ?? 'Find your people'}
          </h1>
        </header>

        {/* Its placeholder is served, so the words on the screen and the words `MEM-014` AC-4
            pins down are one string. */}
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={response?.searchPlaceholder ?? 'Search events, topics…'}
          label="Search events, topics"
          clearLabel="Clear the search"
          size="member"
          className="w-full md:w-[320px] md:flex-none"
        />
      </div>

      {/* 3 — the atmosphere filter row. Built from the taxonomy vocabulary the API serves, so a
          term the admin console renames or reorders moves here without a deploy. */}
      {response === undefined ? null : (
        <FilterPills
          options={response.atmosphereFilters.map((filter) => ({
            value: filter.value,
            label: filter.label,
          }))}
          value={atmosphere}
          onChange={setAtmosphere}
          label={DISCOVER_SIZE_FILTER_LABEL}
          size="member"
          // One scrolling row on the phone, as the phone design draws its chips, rather than a
          // second line of one pill; wrapping from `md`, where the four sit on one line anyway.
          // The scrollbar is hidden, not the scrolling: a swipe still moves the row, and each chip
          // is a button, so tabbing to one scrolls it into view. A grey track under four pills
          // reads as a rule the design never drew (Sumit, reviewing #526).
          className="mb-6 mt-5 flex-nowrap overflow-x-auto [scrollbar-width:none] md:flex-wrap md:overflow-visible [&::-webkit-scrollbar]:hidden"
        />
      )}

      {/* 4 — the list: two columns on the desktop, one on the phone. */}
      {query.isPending ? (
        <div className="mt-5 grid gap-6 md:grid-cols-2" aria-hidden>
          <Skeleton className="h-[240px] w-full rounded-xl" />
          <Skeleton className="h-[240px] w-full rounded-xl" />
        </div>
      ) : response === undefined ? null : response.items.length === 0 ? (
        <EmptyListRegion
          {...emptyListCopy({
            emptyMessage: response.emptyMessage,
            atmosphereLabel: activeFilterLabel,
          })}
        />
      ) : (
        <ul className="grid items-start gap-6 md:grid-cols-2">
          {response.items.map((card) => (
            <li key={card.eventId} className="min-w-0">
              <EventCard card={card} goingCount={goingByEvent.get(card.eventId)} />
            </li>
          ))}
        </ul>
      )}

      {/*
        The bound, when one bit. A discovery list that is missing events and does not say so tells
        a member nothing is on — which is a claim nobody established. The sentence is composed by
        the API so there is one of it.
      */}
      {response?.bound.complete === false ? (
        <p className="mt-6 flex items-start gap-2 rounded-lg bg-muted/60 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{response.bound.incompleteReason}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The list region when it is empty — the prototype's **inline empty** pattern (`padding:56px
 * 20px`, centred, a 38px `search-x` in `--border-strong`, the sentence at `var(--text-md)` in
 * `--text-muted`) rather than a card, because it replaces the grid and is not a thing on the page.
 */
function EmptyListRegion({
  title,
  description,
}: {
  readonly title: string;
  readonly description: string;
}): React.ReactElement {
  return (
    <div className="px-5 py-14 text-center text-muted-foreground" role="status">
      <SearchX className="mx-auto h-[38px] w-[38px] text-border-strong" aria-hidden />
      <p className="mt-3.5 text-md">{title}</p>
      <p className="mt-1 text-sm">{description}</p>
    </div>
  );
}
